import { RequestStatus } from '@features/request/requestSlice';
import { type TUseRequestUpdaterProps, useRequest } from '@features/request/useRequest';
import { useResetStoreData } from '@features/request/useResetStoreData';
import type { CatalogRatioData, CatalogRatioGroupData, CatalogRatiosQuery } from '@gql/graphql';
import { RatioTypeEnum, SortEnumType } from '@gql/graphql';

import type { SortChangedEvent } from 'ag-grid-community';
import { debounce } from 'debounce';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { catalogRatiosGql } from '../../CatalogRatios';
import { getOrderValues, getWhereSearch } from '../helpers';
import type { TOrderState } from '../types';

import { useCatalogRatiosSubgroupsData } from './useCatalogRatiosSubgroupsData';

type TUseBaseCostRatioDataProps = {
    filters: any;
    ratioType: RatioTypeEnum;
    selectedRatioGroup?: CatalogRatioGroupData | null;
    isConstructionWorksGroupSelected: boolean;
    groupsStatus: RequestStatus;
};

const initialState: Record<RatioTypeEnum, CatalogRatioData[] | null> = {
    [RatioTypeEnum.ConstraintCoef]: null,
    [RatioTypeEnum.TechPartCoef]: null,
    [RatioTypeEnum.DismantlingCoef]: null,
};

const requestId = 'baseCostRatioData';

const getWhereColumnFilters = (filters: any) =>
    Object.keys(filters).reduce((where: any, fieldName: string) => {
        const checkedValues = filters[fieldName];

        if (checkedValues?.length) {
            where[fieldName] = {
                or: checkedValues.map((item: string) => ({
                    eq: item,
                })),
            };
        }

        return where;
    }, {});

export const useBaseCostRatioData = (props: TUseBaseCostRatioDataProps) => {
    const { filters, ratioType, selectedRatioGroup, isConstructionWorksGroupSelected, groupsStatus } = props;

    const [isRequestDone, setIsRequestDone] = useState(false);
    const [data, setData] = useState(initialState);
    const [searchString, setSearchString] = useState<string | null>('');
    const [searchWhere, setSearchWhere] = useState<any>();
    const [responseRatioType, setResponseRatioType] = useState<RatioTypeEnum>();
    const [responseGroupId, setResponseGroupId] = useState<string | number>();
    const [order, setOrder] = useState<TOrderState | TOrderState[] | undefined>(undefined);

    const dispatch = useDispatch();

    const [coefficientsReset] = useResetStoreData({ requestId });

    const ratioGroupId = useMemo(() => selectedRatioGroup?.id, [selectedRatioGroup?.id]);

    const { subgroupsData, subgroupsStatus } = useCatalogRatiosSubgroupsData({
        ratioGroupId,
    });

    const subGroupsNames = useMemo(() => subgroupsData.map((subgroup) => subgroup.name), [subgroupsData]);

    const reset = useCallback(() => {
        coefficientsReset(undefined);
    }, [coefficientsReset]);

    const variables = useMemo(
        () => ({
            where: {
                ratioType: { eq: ratioType },
                ...(ratioType !== RatioTypeEnum.TechPartCoef && !isConstructionWorksGroupSelected
                    ? {
                          catalogRatioGroupId: { eq: ratioGroupId },
                      }
                    : {}),
                ...(ratioType === RatioTypeEnum.DismantlingCoef &&
                isConstructionWorksGroupSelected &&
                subGroupsNames.length
                    ? {
                          and: [
                              {
                                  catalogRatioGroupName: {
                                      in: subGroupsNames,
                                  },
                              },
                          ],
                      }
                    : {}),
                ...getWhereColumnFilters(filters[ratioType]),
                ...searchWhere,
            },
            ...(ratioType === RatioTypeEnum.TechPartCoef
                ? { order: order?.length ? order : [{ code: SortEnumType.Asc }] }
                : { order }),
        }),
        [ratioType, isConstructionWorksGroupSelected, ratioGroupId, subGroupsNames, filters, searchWhere, order],
    );

    const effect = useMemo(() => {
        if (
            (isRequestDone ||
                subgroupsStatus === undefined ||
                subgroupsStatus === RequestStatus.FETCHING ||
                groupsStatus === undefined ||
                groupsStatus === RequestStatus.FETCHING) &&
            ratioType !== RatioTypeEnum.TechPartCoef
        ) {
            return false;
        }
        if (ratioType !== RatioTypeEnum.TechPartCoef) {
            if (ratioGroupId && groupsStatus === RequestStatus.SUCCESS) {
                if (ratioType === RatioTypeEnum.DismantlingCoef) {
                    return !(
                        isConstructionWorksGroupSelected &&
                        (subgroupsStatus !== RequestStatus.SUCCESS || !subgroupsData.length)
                    );
                }

                return true;
            }

            return false;
        }

        return true;
    }, [
        ratioType,
        ratioGroupId,
        isConstructionWorksGroupSelected,
        groupsStatus,
        subgroupsStatus,
        subgroupsData.length,
        isRequestDone,
    ]);

    const onSuccess = useCallback(
        (depsVars: TUseRequestUpdaterProps<CatalogRatiosQuery>) => {
            setData({
                ...initialState,
                [ratioType]: depsVars?.payload?.catalogRatios?.items || [],
            });
            setResponseRatioType(ratioType);
            if (ratioGroupId) {
                setResponseGroupId(ratioGroupId);
            } else {
                setResponseGroupId(undefined);
            }
            reset();
        },
        [ratioGroupId, ratioType, reset],
    );

    const [, , , ratiosMakeRequest] = useRequest({
        requestId,
        query: catalogRatiosGql,
        variables,
        withEffect: false,
        dependenciesUpdater: onSuccess,
    });

    const changeOrderHandler = useCallback((params: SortChangedEvent) => {
        const columnsState = params?.api?.getColumnState();
        const { sort, colId } = columnsState?.find((item) => item.sort !== null) ?? {};

        setOrder((prevState) => getOrderValues(sort, colId, prevState));
        setIsRequestDone(false);
    }, []);

    const request = useCallback(() => {
        if (effect) {
            dispatch(ratiosMakeRequest(variables));
            setIsRequestDone(true);
        }
    }, [dispatch, effect, ratiosMakeRequest, variables]);

    const debouncedSearch = useMemo(
        () =>
            debounce((value: string) => {
                setSearchWhere(getWhereSearch(value));
                setIsRequestDone(false);
            }, 500),
        [],
    );
    const handleChange = useCallback(
        ({ value }) => {
            setSearchString(value);
            debouncedSearch(value);
        },
        [debouncedSearch],
    );

    useEffect(() => {
        setIsRequestDone(false);
    }, [filters]);

    useEffect(() => {
        request();
    }, [isRequestDone, request]);

    useEffect(() => {
        setData(initialState);
        reset();
    }, [request, reset]);

    return {
        searchString,
        handleChangeSearchString: handleChange,
        reset,
        data,
        responseRatioType,
        responseGroupId,
        subGroupsNames,
        changeOrderHandler,
        setIsRequestDone,
    };
};
